
import { useEffect } from 'react';

interface HistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function HistoryModal({ isOpen, onClose }: HistoryModalProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in">
      {/* Overlay */}
      <div 
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      ></div>

      {/* Modal */}
      <div className="relative bg-dark-card border border-dark-border rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl animate-scale-in">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-6 right-6 w-10 h-10 flex items-center justify-center text-gray-400 hover:text-white hover:bg-dark-bg rounded-full transition-all duration-300 z-10"
        >
          <i className="ri-close-line text-2xl"></i>
        </button>

        {/* Header Image */}
        <div className="relative h-64 overflow-hidden rounded-t-2xl">
          <img
            src="https://public.readdy.ai/ai/img_res/c6e9ac782144bf1774c2352b9ab4a4b2.jpg"
            alt="Notre Histoire"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-dark-card via-dark-card/50 to-transparent"></div>
          <div className="absolute bottom-8 left-8">
            <h2 className="font-serif text-5xl font-bold text-primary mb-2">
              Notre Histoire
            </h2>
            <p className="text-gray-300 text-lg">
              De Shine&Wash à LOUATI AUTOCENTER
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="p-8 lg:p-12 space-y-8">
          {/* 2023 - Les Débuts */}
          <div className="flex gap-6 animate-fade-in">
            <div className="flex-shrink-0">
              <div className="w-16 h-16 flex items-center justify-center bg-primary/10 border-2 border-primary rounded-full">
                <span className="text-primary font-bold text-lg">2023</span>
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-2xl font-bold text-white mb-3">
                Les Débuts - Shine&Wash
              </h3>
              <p className="text-gray-400 leading-relaxed mb-4">
                Tout a commencé en 2023 avec <strong className="text-primary">Shine&Wash</strong>, une passion pour l'automobile et un rêve : offrir des services de detailing automobile d'exception en Suisse. Dès le départ, notre engagement était clair : qualité irréprochable, attention aux détails et satisfaction client absolue.
              </p>
              <p className="text-gray-400 leading-relaxed">
                Avec des prestations de nettoyage, polish et protection céramique, nous avons rapidement gagné la confiance de nos premiers clients grâce à notre professionnalisme et notre expertise technique.
              </p>
            </div>
          </div>

          {/* Évolution */}
          <div className="flex gap-6 animate-fade-in" style={{animationDelay: '0.1s'}}>
            <div className="flex-shrink-0">
              <div className="w-16 h-16 flex items-center justify-center bg-accent-sky/10 border-2 border-accent-sky rounded-full">
                <i className="ri-arrow-up-line text-accent-sky text-2xl"></i>
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-2xl font-bold text-white mb-3">
                Évolution et Croissance
              </h3>
              <p className="text-gray-400 leading-relaxed mb-4">
                Face à une demande croissante et des retours clients exceptionnels, nous avons décidé d'élargir notre gamme de services. De simple centre de detailing, nous sommes devenus un <strong className="text-accent-sky">centre automobile complet</strong>.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div className="bg-dark-bg border border-dark-border rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <i className="ri-car-washing-line text-primary text-xl"></i>
                    <span className="text-white font-semibold">Detailing Premium</span>
                  </div>
                  <p className="text-gray-400 text-sm">Nettoyage, polish, céramique</p>
                </div>
                <div className="bg-dark-bg border border-dark-border rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <i className="ri-car-line text-accent-sky text-xl"></i>
                    <span className="text-white font-semibold">Vente de Véhicules</span>
                  </div>
                  <p className="text-gray-400 text-sm">Sélection qualité garantie</p>
                </div>
              </div>
            </div>
          </div>

          {/* LOUATI AUTOCENTER */}
          <div className="flex gap-6 animate-fade-in" style={{animationDelay: '0.2s'}}>
            <div className="flex-shrink-0">
              <div className="w-16 h-16 flex items-center justify-center bg-primary/10 border-2 border-primary rounded-full">
                <i className="ri-star-line text-primary text-2xl"></i>
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-2xl font-bold text-white mb-3">
                LOUATI AUTOCENTER - Une Nouvelle Ère
              </h3>
              <p className="text-gray-400 leading-relaxed mb-4">
                Pour refléter notre évolution et notre ambition, <strong className="text-primary">Shine&Wash</strong> est devenu <strong className="text-primary">LOUATI AUTOCENTER</strong>. Un nouveau nom, mais les mêmes valeurs : excellence, professionnalisme et passion automobile.
              </p>
              <p className="text-gray-400 leading-relaxed mb-4">
                Aujourd'hui, nous sommes fiers d'offrir une gamme complète de services automobiles à Payerne et dans toute la région :
              </p>
              <ul className="space-y-2">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-primary text-xl mt-0.5"></i>
                  <span className="text-gray-300">Prestations detailing premium (nettoyage, polish, céramique)</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-primary text-xl mt-0.5"></i>
                  <span className="text-gray-300">Vente de véhicules de qualité avec garantie</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-primary text-xl mt-0.5"></i>
                  <span className="text-gray-300">Reprise et estimation de votre véhicule</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-primary text-xl mt-0.5"></i>
                  <span className="text-gray-300">Service à domicile pour votre confort</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Notre Engagement */}
          <div className="bg-gradient-to-br from-primary/10 to-accent-sky/10 border border-primary/20 rounded-xl p-6 animate-fade-in" style={{animationDelay: '0.3s'}}>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 flex items-center justify-center bg-primary rounded-full flex-shrink-0">
                <i className="ri-heart-line text-dark-bg text-xl"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-2">
                  Notre Engagement Aujourd'hui
                </h3>
                <p className="text-gray-300 leading-relaxed">
                  Chez LOUATI AUTOCENTER, nous continuons à placer la satisfaction client au cœur de nos priorités. Chaque véhicule est traité avec le plus grand soin, chaque prestation est réalisée avec passion et expertise. Notre objectif ? Dépasser vos attentes à chaque visite.
                </p>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 animate-fade-in" style={{animationDelay: '0.4s'}}>
            <div className="text-center p-4 bg-dark-bg border border-dark-border rounded-lg">
              <div className="text-3xl font-bold text-primary mb-1">2023</div>
              <div className="text-sm text-gray-400">Année de création</div>
            </div>
            <div className="text-center p-4 bg-dark-bg border border-dark-border rounded-lg">
              <div className="text-3xl font-bold text-accent-sky mb-1">100%</div>
              <div className="text-sm text-gray-400">Satisfaction client</div>
            </div>
            <div className="text-center p-4 bg-dark-bg border border-dark-border rounded-lg">
              <div className="text-3xl font-bold text-primary mb-1">Expert</div>
              <div className="text-sm text-gray-400">Detailing premium</div>
            </div>
            <div className="text-center p-4 bg-dark-bg border border-dark-border rounded-lg">
              <div className="text-3xl font-bold text-accent-sky mb-1">Payerne</div>
              <div className="text-sm text-gray-400">Suisse</div>
            </div>
          </div>

          {/* CTA */}
          <div className="flex flex-col sm:flex-row gap-4 pt-4 animate-fade-in" style={{animationDelay: '0.5s'}}>
            <a
              href="/reservation"
              className="flex-1 flex items-center justify-center gap-3 bg-primary text-dark-bg px-6 py-4 rounded-lg font-semibold hover:bg-primary/90 transition-all duration-300 whitespace-nowrap transform hover:scale-105"
            >
              <span>Réserver une prestation</span>
              <i className="ri-arrow-right-line"></i>
            </a>
            <a
              href="tel:+41796840588"
              className="flex-1 flex items-center justify-center gap-3 border-2 border-primary text-primary px-6 py-4 rounded-lg font-semibold hover:bg-primary hover:text-dark-bg transition-all duration-300 whitespace-nowrap transform hover:scale-105"
            >
              <i className="ri-phone-line"></i>
              <span>Nous contacter</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
